// ═══════════════════════════════════════════════════════════════
// PIXALLOY — Touch Gesture Hook (Phase 2.3)
// ═══════════════════════════════════════════════════════════════
// Adds multi-touch gestures for tablet mode without wrapping
// single-finger events. Returns additional event handlers that
// should be combined with the existing pointer handlers.
//
//   2 fingers → pinch-to-zoom + two-finger pan
//   3 fingers → swipe left=undo, right=redo
// ═══════════════════════════════════════════════════════════════

import { useRef, useEffect } from "react";

export function useTouchGestures({
  panState, applyVPTransform, isTablet,
  vpContainerRef,
  pc, C, fu, zoomToFit,
}) {
  const touchesRef = useRef(new Map());
  const gestureActiveRef = useRef(false);
  const lastPinchRef = useRef(null);
  const lastTapRef = useRef({ time: 0, x: 0, y: 0 }); // for double-tap detection

  useEffect(() => {
    if (!isTablet) return;
    const el = vpContainerRef?.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (e.touches.length >= 2) {
        e.preventDefault();
        gestureActiveRef.current = true;
        touchesRef.current.clear();
        for (let i = 0; i < e.touches.length; i++) {
          const t = e.touches[i];
          touchesRef.current.set(t.identifier, { x: t.clientX, y: t.clientY, startX: t.clientX, startY: t.clientY });
        }
        if (e.touches.length === 2) {
          const pts = [...touchesRef.current.values()];
          const dx = pts[1].x - pts[0].x, dy = pts[1].y - pts[0].y;
          lastPinchRef.current = {
            dist: Math.sqrt(dx * dx + dy * dy),
            midX: (pts[0].x + pts[1].x) / 2,
            midY: (pts[0].y + pts[1].y) / 2,
          };
        }
      } else if (e.touches.length === 1) {
        // Track single touch for double-tap detection
        touchesRef.current.clear();
        touchesRef.current.set(e.touches[0].identifier, {
          x: e.touches[0].clientX, y: e.touches[0].clientY,
          startX: e.touches[0].clientX, startY: e.touches[0].clientY,
          time: Date.now(),
        });
      }
    };

    const onTouchMove = (e) => {
      if (!gestureActiveRef.current) return;
      if (e.touches.length < 2) return;
      e.preventDefault();

      for (let i = 0; i < e.touches.length; i++) {
        const t = e.touches[i];
        const tracked = touchesRef.current.get(t.identifier);
        if (tracked) { tracked.x = t.clientX; tracked.y = t.clientY; }
      }

      if (e.touches.length === 2 && lastPinchRef.current) {
        const pts = [...touchesRef.current.values()];
        if (pts.length < 2) return;
        const dx = pts[1].x - pts[0].x, dy = pts[1].y - pts[0].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const midX = (pts[0].x + pts[1].x) / 2;
        const midY = (pts[0].y + pts[1].y) / 2;

        const prev = lastPinchRef.current;

        if (prev.dist > 10 && dist > 10) {
          const scale = dist / prev.dist;
          panState.current.zoom = Math.max(0.1, Math.min(8, panState.current.zoom * scale));
        }

        panState.current.panX += midX - prev.midX;
        panState.current.panY += midY - prev.midY;

        lastPinchRef.current = { dist, midX, midY };
        applyVPTransform();
      }
    };

    const onTouchEnd = (e) => {
      // 3-finger swipe detection on release
      if (gestureActiveRef.current && touchesRef.current.size >= 3 && e.touches.length === 0) {
        const pts = [...touchesRef.current.values()];
        let totalDx = 0;
        for (const p of pts) totalDx += p.x - p.startX;
        const avgDx = totalDx / pts.length;
        if (Math.abs(avgDx) > 50) {
          if (avgDx < 0 && pc && C) { pc(C.UNDO); fu?.(); }
          else if (pc && C) { pc(C.REDO); fu?.(); }
        }
      }

      // Double-tap to zoom-fit detection (single finger, quick tap, no movement)
      if (!gestureActiveRef.current && e.touches.length === 0 && touchesRef.current.size === 1) {
        const t = [...touchesRef.current.values()][0];
        if (t && t.time) {
          const dur = Date.now() - t.time;
          const dist = Math.sqrt((t.x - t.startX) ** 2 + (t.y - t.startY) ** 2);
          if (dur < 300 && dist < 15) {
            // This is a tap — check for double-tap
            const now = Date.now();
            const last = lastTapRef.current;
            const tapGap = now - last.time;
            const tapDist = Math.sqrt((t.x - last.x) ** 2 + (t.y - last.y) ** 2);
            if (tapGap < 400 && tapDist < 30) {
              // Double-tap detected — zoom to fit
              if (zoomToFit) zoomToFit();
              lastTapRef.current = { time: 0, x: 0, y: 0 }; // reset to prevent triple-tap
            } else {
              lastTapRef.current = { time: now, x: t.x, y: t.y };
            }
          }
        }
      }

      if (e.touches.length === 0) {
        gestureActiveRef.current = false;
        touchesRef.current.clear();
        lastPinchRef.current = null;
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: false });
    el.addEventListener("touchcancel", onTouchEnd, { passive: false });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isTablet, panState, applyVPTransform, pc, C, fu, zoomToFit]);

  // Expose gestureActive so canvas can suppress single-pointer tool actions during pinch
  return { gestureActiveRef };
}
